<script setup>
import StatusChip from '@/components/shared/StatusChip.vue'
import { notificationPurposeLabel } from '@/stores/notificationTemplates'
import { notificationFailureReasonLabel, useNotificationsStore } from '@/stores/notifications'

definePage({
  meta: {
    navActiveLink: 'notifications',
  },
})

const store = useNotificationsStore()

const purposeOptions = [
  'appointment_reminder',
  'booking_confirmation',
  'schedule_update',
  'payment_reminder',
  'motivational_message',
  'coaching_update',
]

const selected = ref(null)

onMounted(() => {
  store.fetchNotifications().catch(() => null)
})

const applyFilters = () => {
  store.fetchNotifications().catch(() => null)
}

const formatWhen = value => {
  if (!value)
    return '—'
  try {
    return new Intl.DateTimeFormat('nl-NL', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value))
  }
  catch {
    return value
  }
}

const openDetail = item => {
  selected.value = item
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <h1 class="text-h4 mb-1">
          {{ $t('notifications.title') }}
        </h1>
        <p class="text-medium-emphasis mb-0">
          {{ $t('notifications.subtitle') }}
        </p>
      </div>
      <VBtn
        variant="tonal"
        :to="{ name: 'notification-templates' }"
      >
        {{ $t('notifications.manageTemplates') }}
      </VBtn>
    </div>

    <VAlert
      v-if="store.error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <VCard
      class="mb-4"
      flat
      border
    >
      <VCardText class="d-flex flex-wrap gap-4">
        <VSelect
          v-model="store.filters.lifecycle_state"
          :items="[
            { title: $t('notifications.filters.allStatuses'), value: '' },
            { title: $t('notifications.status.scheduled'), value: 'scheduled' },
            { title: $t('notifications.status.sent'), value: 'sent' },
            { title: $t('notifications.status.delivered'), value: 'delivered' },
            { title: $t('notifications.status.read'), value: 'read' },
            { title: $t('notifications.status.failed'), value: 'failed' },
          ]"
          :label="$t('notifications.filters.status')"
          density="comfortable"
          style="max-inline-size: 220px; min-inline-size: 180px;"
          @update:model-value="applyFilters"
        />
        <VSelect
          v-model="store.filters.purpose"
          :items="[
            { title: $t('notifications.filters.allPurposes'), value: '' },
            ...purposeOptions.map(p => ({ title: notificationPurposeLabel(p), value: p })),
          ]"
          :label="$t('notifications.filters.purpose')"
          density="comfortable"
          style="max-inline-size: 280px; min-inline-size: 200px;"
          @update:model-value="applyFilters"
        />
      </VCardText>
    </VCard>

    <div
      v-if="store.loading"
      class="text-medium-emphasis"
      role="status"
    >
      {{ $t('notifications.loading') }}
    </div>

    <VAlert
      v-else-if="store.isEmpty"
      type="info"
      variant="tonal"
      role="status"
    >
      {{ $t('notifications.empty') }}
    </VAlert>

    <VTable
      v-else
      class="text-no-wrap"
    >
      <thead>
        <tr>
          <th scope="col">
            {{ $t('notifications.columns.client') }}
          </th>
          <th scope="col">
            {{ $t('notifications.columns.purpose') }}
          </th>
          <th scope="col">
            {{ $t('notifications.columns.subject') }}
          </th>
          <th scope="col">
            {{ $t('notifications.columns.status') }}
          </th>
          <th scope="col">
            {{ $t('notifications.columns.when') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="item in store.notifications"
          :key="item.id"
          style="cursor: pointer;"
          tabindex="0"
          @click="openDetail(item)"
          @keydown.enter="openDetail(item)"
        >
          <td>{{ item.client_name || item.recipient_email || '—' }}</td>
          <td>{{ notificationPurposeLabel(item.purpose) }}</td>
          <td>{{ item.subject }}</td>
          <td>
            <StatusChip
              domain="notification"
              :state="item.lifecycle_state"
            />
          </td>
          <td>{{ formatWhen(item.sent_at || item.scheduled_for) }}</td>
        </tr>
      </tbody>
    </VTable>

    <VDialog
      :model-value="!!selected"
      max-width="640"
      @update:model-value="val => { if (!val) selected = null }"
    >
      <VCard v-if="selected">
        <VCardTitle>{{ selected.subject }}</VCardTitle>
        <VCardText>
          <div class="mb-3">
            <StatusChip
              domain="notification"
              :state="selected.lifecycle_state"
            />
          </div>
          <p class="text-medium-emphasis mb-2">
            {{ notificationPurposeLabel(selected.purpose) }}
            ·
            {{ selected.client_name || selected.recipient_email }}
          </p>
          <p class="mb-2">
            <strong>{{ $t('notifications.columns.when') }}:</strong>
            {{ formatWhen(selected.sent_at || selected.scheduled_for) }}
          </p>
          <p
            v-if="selected.failure_reason"
            class="mb-2"
            role="alert"
          >
            <strong>{{ $t('notifications.failureReason') }}:</strong>
            {{ notificationFailureReasonLabel(selected.failure_reason) }}
          </p>
          <VDivider class="my-4" />
          <pre class="text-body-2 notification-body">{{ selected.body }}</pre>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="selected = null"
          >
            {{ $t('common.close') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.notification-body {
  white-space: pre-wrap;
  font-family: inherit;
  margin: 0;
}
</style>
